import React, { useCallback, useEffect, useState } from "react";
import { AutoComplete, Button, Col, Input, Row, Space, Typography, Select, Tooltip } from "antd";
import { debounce } from "lodash";
import { useLocation } from "react-router";
import { createStructuredSelector } from "reselect";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import { compose } from "redux";
import { connect, useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import InputMask from "react-input-mask";
import { displayedMRN } from "@tailormed/common-client/util/mrn";

import NoResultIcon from "../assets/svg/NoResultIcon";
import { ShorterTextWithTooltip, HighlightResult } from "./ShorterText";
import {
  formatPhoneNumberWhileTyping,
  formatMaskedSSN,
  decodeDate,
  formatPhoneNumber,
  decodeNumbersForSearch
} from "../utils/formaters";
import { stripHtml } from "@tailormed/common-client/util/formatters";
import { selectFirstRecentPatients } from "@tailormed/common-client/store/reselectSelector";
import { selectSearchedPatients, selectIsFetching, selectAssigneesAndUserGroups } from "../store/selector";
import ACTIONS from "../store/action";
import { ApiNames } from "../api/api";
import { Routes } from "../constant/routes";
import {
  GRAY_100,
  GRAY_1500,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_500,
  GRAY_50,
  GRAY_900,
  PRIMARY_600,
  WHITE,
  DARK_LIVER
} from "../constant/colors";
import { fontWeights, sizes } from "../constant/styles";
import "./HeaderSearchBar.css";
import { useGetSetting } from "../hooks/getSetting";
import { customerTypes } from "../constant/customerType";
import { HistoryOutlined, LoadingOutlined } from "@ant-design/icons";

const searchTypes = Object.freeze({
  name_id: {
    key: "name_id",
    maxLength: null,
    maskInput: ""
  },
  dob: {
    key: "dob",
    maxLength: 10,
    maskInput: "99/99/9999"
  },
  phone_number: {
    key: "phone_number",
    maxLength: 14,
    maskInput: "(999) 999-9999"
  },
  social_security_number: {
    key: "social_security_number",
    maxLength: 11,
    maskInput: "999-99-9999"
  }
});

const { Text } = Typography;
const InputGroup = Input.Group;
const { Option } = Select;
const HEADER_WIDTH = 563;
const DROP_DOWN_LIST_WIDTH = 112;

const NAME_ID = searchTypes.name_id.key;
const DOB = searchTypes.dob.key;
const PHONE = searchTypes.phone_number.key;
const SSN = searchTypes.social_security_number.key;
const HOME_NUMBER = "home_number";
const ONE_YEAR = 31536000; // in seconds: 60*60*24*365

//const SearchIconBase64 = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(searchIconSvg)))}`;

const StyledInputGroup = styled(InputGroup)`
  border: 1px solid ${GRAY_300};
  border-radius: 5px;
  height: 54px;

  @media (max-width: 1600px) {
    width: 632px !important;
  }

  @media (min-width: 1600px) {
    width: 800px !important;
  }

  &:hover {
    border: 1px solid ${GRAY_400};
  }

  &:focus {
    border: 1px solid ${PRIMARY_600};
  }

  &:focus-within {
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    border: 1px solid ${PRIMARY_600};
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    height: 51px;
    background: ${GRAY_50};
    align-items: center;
    color: ${GRAY_900};
    border: 0px;
    border-right: 1px solid ${GRAY_300};
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    font-size: ${sizes.medium};

    &:hover {
      background: ${GRAY_100};
    }
    &:focus-within {
      border-color: transparent;
      box-shadow: none;
      border-right: 1px solid #d0d5dd;
    }
  }

  .ant-select-single.ant-select-open .ant-select-selection-item {
    color: ${GRAY_900};
    background: ${GRAY_100};
    box-shadow: 0px;
    border: 0px;
    outline-color: transparent;
    outline-offset: unset;
    outline-width: 0px;
  }

  .ant-select-arrow {
    color: ${GRAY_900};
  }

  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    height: 51px;
    padding-left: 12px;
    background: ${WHITE};
    border: 0px;
    border-left: 1px solid ${GRAY_300};
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    border-radius: 0px 4px 4px 0px;
    font-size: ${sizes.medium};
    color: ${GRAY_900};
    &:focus {
      outline-color: transparent;
      outline-offset: unset;
      outline-width: 0px;
    }
    &::placeholder {
      color: ${GRAY_400};
    }
  }
`;

const SearchResultsTitle = styled(Text)`
  font-family: Open Sans;
  font-style: normal;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  line-height: 14px;
  letter-spacing: 0.15px;
  color: ${GRAY_500};
`;

const StyledInputMask = styled(InputMask)`
  &::placeholder {
    font-weight: ${fontWeights.regular};
    font-size: ${sizes.medium};
    color: ${GRAY_1500} !important;
  }
`;

const textOptionsListStyle = {
  fontSize: sizes.medium,
  fontWeight: fontWeights.regular,
  color: GRAY_900
};

const StyledPatientInfo = styled.div`
  font-size: ${sizes.medium};
  ${(props) => props.recentPatients && `font-weight: ${fontWeights.semibold};`}
  color: ${GRAY_900};
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding-bottom: 7px;
`;

const StyledPatientMeta = styled.span`
  font-size: ${sizes.small};
  font-weight: ${fontWeights.regular};
  color: ${GRAY_500};
`;

const StyledLoaderContainer = styled.div`
  color: ${PRIMARY_600};
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderSearchbarComponent = ({
  t,
  history,
  isSearchFetching,
  isRecentPatientsFetching,
  searchedPatients,
  firstRecentPatients,
  searchPatientsAct,
  recentPatientsAct,
  setSearchResultPageAct,
  setRecentPatientPageAct,
  restSearchResult
}) => {
  const [cookies, setCookie] = useCookies(["searchBy"]);
  const [searchText, setSearchText] = useState("");

  let [idPrefix, customerType] = useGetSetting(["id_prefix", "fe-customer-type"]);
  customerType = customerType.toLowerCase();
  const searchByDefaultValue = cookies["searchBy"] ?? (customerType === customerTypes.RETAIL ? PHONE : NAME_ID);
  const [searchBy, setSearchBy] = useState(searchByDefaultValue);
  const [startSearching, setStartSearching] = useState(isSearchFetching);
  const [startRecentPatients, setStartRecentPatients] = useState(false);
  const [previousPageSearch, setPreviousPageSearch] = useState(false);
  const [previousPageRecentPatient, setPreviousPageRecentPatient] = useState(false);
  const [previousPageTopOpportunities, setPreviousPageTopOpportunities] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({});
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const { patients = [], totalPatients } = searchText ? searchedPatients || {} : firstRecentPatients || {};
  const NOT_PATIENT_PAGE = !(location.pathname.split("/")[1] !== "patient");
  const SEARCH_RESULTS_PAGE = location.pathname.includes(Routes.SEARCH_RESULTS);
  const RECENT_PATIENT_PAGE = location.pathname.includes(Routes.RECENT_PATIENTS);
  const TOP_OPPORTUNITIES_PAGE = location.pathname.includes(Routes.TOP_OPPORTUNITIES);
  const enablePhoneMasking = searchText?.length < 4 && searchBy === PHONE;
  const searchParams = new URLSearchParams(location.search);
  const searchByParams = searchParams.get("searchBy");
  const searchTextParams = location?.state?.searchText;

  const dispatch = useDispatch();
  const shouldReturnToRelevantPatientAct = (shouldReturn, notificationId) =>
    dispatch(ACTIONS.setShouldReturnToRelevantPatient(shouldReturn, notificationId));
  const setPreviousPageAct = (previousPage) => dispatch(ACTIONS.setPreviousPage(previousPage));

  const assignees = useSelector(selectAssigneesAndUserGroups);

  useEffect(() => {
    if (!SEARCH_RESULTS_PAGE && (patients.length || searchText)) {
      resetAutoComplete();
    }
    // Set search text, as well as set variables for the back button on patient details to properly go back to the search results page
    if (SEARCH_RESULTS_PAGE && searchText) {
      setSearchText(searchTextParams);
      setSearchBy(searchByParams);

      setSearchResultPageAct(true, searchBy, searchText);
      setPreviousPageSearch(true);
    } else if (previousPageSearch) {
      setPreviousPageSearch(false);
    } else {
      setSearchResultPageAct(false, "", "");
    }

    // Set variables for the back button on patient details to properly go back to the recent patients page
    if (RECENT_PATIENT_PAGE) {
      setRecentPatientPageAct(true);
      setPreviousPageRecentPatient(true);
    } else if (previousPageRecentPatient) {
      setPreviousPageRecentPatient(false);
    } else {
      setRecentPatientPageAct(false);
    }

    // Set variables for the back button on patient details and recent patients page to properly go back to the relevant patients page
    if (TOP_OPPORTUNITIES_PAGE) {
      shouldReturnToRelevantPatientAct(true, location.pathname.split("/")[2]);
      setPreviousPageTopOpportunities(true);
    } else if (previousPageTopOpportunities) {
      setPreviousPageTopOpportunities(false);
    } else {
      shouldReturnToRelevantPatientAct(false, null);
    }

    // Set current and previous location for back buttons
    if (!SEARCH_RESULTS_PAGE || (SEARCH_RESULTS_PAGE && searchText)) {
      setPreviousPageAct(currentLocation);
      setCurrentLocation(location);
    }
  }, [location, searchTextParams]);

  useEffect(() => {
    setSearchBy(searchByDefaultValue);
  }, [customerType]);

  useEffect(() => {
    if ([DOB, PHONE, SSN].includes(searchBy)) {
      searchText?.length != searchTypes[searchBy]?.maxLength && restSearchResult();
    }
    if (searchText === "(" && searchBy === PHONE) setSearchText("");
  }, [searchText, searchBy]);

  // Note: we need to sync isSearchFetching state to control the no content found section
  useEffect(() => {
    setStartSearching(isSearchFetching);
  }, [isSearchFetching]);
  useEffect(() => {
    setStartRecentPatients(isRecentPatientsFetching);
  }, [isRecentPatientsFetching]);

  const handleSearch = (text) => {
    const textParamIsString = typeof text === "string";

    // This is usually because we clicked in and the value of text is a click event
    // Setting it to the current search query allows the box to not get erased when clicking in
    if (!textParamIsString) {
      text = searchText;
    }

    let formatedQuery = text ? text : "";
    let shouldFetchQuery = false;

    switch (searchBy) {
      case SSN:
      case PHONE: {
        (!formatedQuery?.length || formatedQuery?.length === searchTypes[searchBy]?.maxLength) &&
          (text ? searchPatientsAct(1, decodeNumbersForSearch(formatedQuery), searchBy) : recentPatientsAct(1),
          (shouldFetchQuery = true));

        break;
      }
      case DOB: {
        (!formatedQuery?.length || formatedQuery?.length === searchTypes[searchBy]?.maxLength) &&
          (text ? searchPatientsAct(1, formatedQuery, searchBy) : recentPatientsAct(1), (shouldFetchQuery = true));
        break;
      }
      case NAME_ID: {
        if (formatedQuery) {
          setStartSearching(true);
        } else {
          setStartRecentPatients(true);
        }
        debouncedSearch(formatedQuery, searchBy);
        shouldFetchQuery = true;
        break;
      }
    }

    if (!open) {
      setOpen(true);
    }
    setSearchText(formatedQuery);

    if (SEARCH_RESULTS_PAGE && shouldFetchQuery) {
      history.replace(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText: formatedQuery });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (patients.length > 0) {
        setSearchText(searchText);
        if (SEARCH_RESULTS_PAGE) {
          history.replace(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText });
        } else {
          history.push(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText });
        }
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce((nextValue, searchBy) => {
      nextValue ? searchPatientsAct(1, nextValue, searchBy) : recentPatientsAct(1);
    }, 500),
    []
  );

  const handleChangeSelect = (value) => {
    setSearchBy(value);
    setSearchText("");
    debouncedSearch("", searchBy);
    setCookie("searchBy", value, { path: "/", maxAge: ONE_YEAR });
  };

  const resetAutoComplete = () => {
    setSearchText("");
    restSearchResult();
  };

  const handlePaste = (e) => {
    let formatedQuery = e.clipboardData.getData("text");

    if (formatedQuery.length === 10 && !formatedQuery.includes("(") && searchBy === PHONE) {
      searchPatientsAct(1, decodeNumbersForSearch(formatedQuery), searchBy);
      setSearchText(formatPhoneNumberWhileTyping(formatedQuery));
    }
  };

  const menuPendoId = searchText ? "patient_search_menu" : "recent_patients_menu";
  const showMorePendoId = searchText ? "header_search_show_more" : "header_recent_patients_show_more";

  return (
    <StyledInputGroup
      compact
      style={{
        width: HEADER_WIDTH,
        display: "flex",
        marginRight: NOT_PATIENT_PAGE ? 42 : 0
      }}
    >
      <Select
        defaultValue={searchByDefaultValue}
        value={searchBy}
        onChange={handleChangeSelect}
        placement="bottomRight"
        dropdownMatchSelectWidth={DROP_DOWN_LIST_WIDTH}
        dropdownStyle={{ position: "fixed" }}
      >
        <Option value={NAME_ID} id="searchbar_name">
          {t("header_searchbar.option_type_list.name_id")}
        </Option>
        <Option value={DOB} id="searchbar_dob">
          {t("header_searchbar.option_type_list.dob")}
        </Option>
        <Option value={PHONE} id="searchbar_phone">
          {t("header_searchbar.option_type_list.phone")}
        </Option>
        <Option value={SSN} id="searchbar_ssn">
          {t("header_searchbar.option_type_list.ssn")}
        </Option>
      </Select>

      {open && !searchText && (
        <div
          style={{
            position: "absolute",
            right: "15px",
            top: "13px",
            zIndex: 1000,
            fontSize: sizes.large,
            fontWeight: fontWeights.bold
          }}
        >
          <HistoryOutlined />
        </div>
      )}

      <AutoComplete
        id="main_header_search_patient"
        autoFocus={searchText && true}
        loading={isSearchFetching}
        onPaste={handlePaste}
        onSelect={() => {
          // Set timeout here so the blur works on some computers
          setTimeout(() => {
            document.getElementById("main_header_search_patient").blur();
          }, 20);
        }}
        notFoundContent={
          searchText &&
          !startSearching && (
            <>
              {totalPatients === undefined && (
                <Text style={{ color: DARK_LIVER, textAlign: "left", width: "100%" }}>
                  {t(`header_searchbar.autocomplete_search_results_list.${searchBy}`)}
                </Text>
              )}
              <Space direction="vertical" style={{ width: "100%", height: "100%", alignItems: "center" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: GRAY_400
                  }}
                >
                  <NoResultIcon />
                </div>
                {totalPatients !== undefined && (
                  <Text
                    id="header_search_no_results"
                    style={{
                      textAlign: "center",
                      fontSize: sizes.medium_large,
                      fontWeight: fontWeights.semibold,
                      color: GRAY_400
                    }}
                  >
                    {t(`header_searchbar.autocomplete_search_results_list.no_results`)}
                  </Text>
                )}
              </Space>
            </>
          )
        }
        style={{ flexBasis: "100%" }}
        dropdownStyle={{
          borderRadius: "8px",
          boxShadow: "0px 12px 16px -4px rgba(16, 24, 40, 0.1), 0px 4px 6px -2px rgba(16, 24, 40, 0.05)",
          background: WHITE,
          padding: "12px 0px",
          position: "fixed"
        }}
        allowClear={!isSearchFetching}
        onSearch={handleSearch}
        onFocus={handleSearch}
        onBlur={() => {
          // This allows us to click show more and it works
          setTimeout(() => {
            setOpen(false);
          }, 200);
        }}
        onDropdownVisibleChange={(isOpen) => {
          if (isOpen) {
            // Scroll the dropdown to the top each time it opens
            setTimeout(function () {
              const lists = document.getElementsByClassName("rc-virtual-list-holder");
              for (let i = 0; i <= lists.length; i++) {
                if (lists.item(i)) {
                  lists.item(i).scrollTop = 0;
                }
              }
            }, 50);
          }
        }}
        value={searchText}
        onKeyDown={handleKeyDown}
        onClear={resetAutoComplete}
        open={open}
        popupClassName={"header_search_dropdown"}
        dropdownRender={(menu) => (
          <>
            <div
              id={menuPendoId}
              className={
                !searchText && !patients.length && (startRecentPatients || isRecentPatientsFetching)
                  ? "patient_search_menu_loading"
                  : ""
              }
            >
              {menu}
            </div>
            {patients.length > 3 && (
              <Row justify="center" style={{ borderTop: `1px solid ${GRAY_200}` }}>
                <Col>
                  <Button
                    id={showMorePendoId}
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => {
                      searchText
                        ? history.push(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText })
                        : history.push(`${Routes.RECENT_PATIENTS}`);
                      setOpen(false);
                    }}
                  >
                    <Text
                      style={{
                        color: PRIMARY_600,
                        cursor: "pointer",
                        fontSize: sizes.medium,
                        fontWeight: fontWeights.semibold,
                        lineHeight: "30px",
                        fontStyle: "normal",
                        marginTop: "5px"
                      }}
                    >
                      {t("header_searchbar.show_more")}
                    </Text>
                  </Button>
                </Col>
              </Row>
            )}
          </>
        )}
        options={
          !searchText && !startRecentPatients && !patients.length
            ? [
                {
                  options: [
                    {
                      value: "notfound",
                      label: (
                        <Space direction="vertical" style={{ width: "100%", height: "100%", alignItems: "center" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              color: GRAY_400
                            }}
                          >
                            <NoResultIcon />
                          </div>
                          <Text
                            id="header_search_no_results"
                            style={{
                              textAlign: "center",
                              fontSize: sizes.medium_large,
                              fontWeight: fontWeights.semibold,
                              color: GRAY_400
                            }}
                          >
                            {t(`header_searchbar.autocomplete_search_results_list.no_results`)}
                          </Text>
                        </Space>
                      )
                    }
                  ]
                }
              ]
            : !searchText && (startRecentPatients || isRecentPatientsFetching)
            ? [
                {
                  label: undefined,
                  options: [
                    {
                      value: "loading",
                      label: (
                        <StyledLoaderContainer>
                          <LoadingOutlined style={{ fontSize: "35px" }} />
                        </StyledLoaderContainer>
                      )
                    }
                  ]
                }
              ]
            : patients.length === 0
            ? []
            : [
                {
                  label: (
                    <>
                      <Row>
                        <Col span={searchBy === PHONE && searchText ? 13 : 14}>
                          <SearchResultsTitle>
                            {searchText
                              ? t("header_searchbar.results_title.name_id")
                              : t("header_searchbar.results_title.recently_viewed")}
                          </SearchResultsTitle>
                        </Col>
                        {searchBy != NAME_ID && searchText && (
                          <Col span={searchBy === PHONE && searchText ? 6 : 5}>
                            <SearchResultsTitle>{t(`header_searchbar.results_title.${searchBy}`)}</SearchResultsTitle>
                          </Col>
                        )}
                        {(searchBy == NAME_ID || !searchText) && (
                          <Col span={searchBy === PHONE && searchText ? 6 : 5}>
                            <SearchResultsTitle>{t(`header_searchbar.results_title.dob`)}</SearchResultsTitle>
                          </Col>
                        )}
                        <Col span={5}>
                          <SearchResultsTitle>{t(`header_searchbar.results_title.assignee`)}</SearchResultsTitle>
                        </Col>
                      </Row>
                    </>
                  ),
                  options: patients.slice(0, 10).map((patient) => {
                    const name = stripHtml(patient.full_name || patient.name || "Patient");
                    const mrn = stripHtml(displayedMRN(patient.mrn, "") || "");
                    const patientId = patient.patientId;
                    const displayedPatientId = stripHtml(`${idPrefix ? `${idPrefix}-` : ""}${patientId}`);
                    const journeyId = patient.journeyId;

                    let assigneeNames = [];
                    for (const patientAssignee of patient.assignees) {
                      const assigneeId = patientAssignee.assignee;
                      const assignee = assignees.find((assignee) => assignee.id === assigneeId);
                      if (assignee) {
                        assigneeNames.push(patientAssignee.userName || assignee.displayName);
                      }
                    }
                    assigneeNames = assigneeNames.join("; ");

                    let mrnRecord =
                      customerType === customerTypes.RETAIL
                        ? searchBy === PHONE
                          ? ""
                          : t("columns.record_phone", { mrnRecord: formatPhoneNumber(mrn) })
                        : t("columns.record_mrn", { mrnRecord: mrn });

                    return {
                      value: patientId.toString(),
                      label: (
                        <Row
                          onClick={() => {
                            setOpen(false);
                            restSearchResult();
                            history.push(`/patient/${patientId}/journey/${journeyId}`);
                          }}
                        >
                          <Col
                            span={searchBy === PHONE && searchText ? 13 : 14}
                            style={{ display: "flex", flexDirection: "column", paddingRight: "10px" }}
                          >
                            <Tooltip
                              title={
                                <Text>
                                  <span>
                                    <StyledPatientInfo
                                      id="tooltip_header_search_patient_name_mrn_id"
                                      recentPatients={!searchText}
                                    >
                                      {name}
                                      <br />
                                      <StyledPatientMeta
                                        id={`tooltip_header_search_patient_${
                                          customerType === customerTypes.RETAIL ? "phone" : "mrn"
                                        }_id`}
                                      >
                                        {mrnRecord} ({displayedPatientId})
                                      </StyledPatientMeta>
                                    </StyledPatientInfo>
                                  </span>
                                </Text>
                              }
                              color={"white"}
                              placement={"left"}
                            >
                              <StyledPatientInfo recentPatients={!searchText}>
                                {!searchText && (
                                  <>
                                    <StyledPatientMeta style={{ fontSize: sizes.medium }}>
                                      <HistoryOutlined />{" "}
                                    </StyledPatientMeta>
                                  </>
                                )}
                                <span
                                  id="header_search_patient_name"
                                  dangerouslySetInnerHTML={{ __html: HighlightResult(name, searchText) }}
                                />{" "}
                                {patient[HOME_NUMBER] && patient[PHONE] && searchBy === PHONE && searchText && <br />}
                                <StyledPatientMeta
                                  id={`header_search_patient_${
                                    customerType === customerTypes.RETAIL ? "phone" : "mrn"
                                  }_id`}
                                >
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: HighlightResult(`${mrnRecord} (${displayedPatientId})`, searchText)
                                    }}
                                  />
                                </StyledPatientMeta>
                              </StyledPatientInfo>
                            </Tooltip>
                          </Col>
                          <Col span={searchBy === PHONE && searchText ? 6 : 5}>
                            {(patient[searchBy] || searchBy === NAME_ID || !searchText) && (
                              <ShorterTextWithTooltip
                                text={
                                  searchBy === DOB || searchBy === NAME_ID || !searchText
                                    ? decodeDate(patient[DOB])
                                    : searchBy === PHONE
                                    ? formatPhoneNumberWhileTyping(patient[PHONE])
                                    : searchBy === SSN
                                    ? formatMaskedSSN(patient[SSN])
                                    : `${patient[searchBy]} - ${searchBy}`
                                }
                                id={`header_search_patient${
                                  searchBy === DOB || searchBy === NAME_ID || !searchText
                                    ? "_dob"
                                    : searchBy === PHONE
                                    ? "_phone_number"
                                    : searchBy === SSN
                                    ? "_ssn"
                                    : ""
                                }`}
                                searchInput={searchText}
                                maxLength={15}
                                textStyle={textOptionsListStyle}
                              />
                            )}
                            {patient[HOME_NUMBER] && searchBy === PHONE && searchText && (
                              <ShorterTextWithTooltip
                                text={formatPhoneNumberWhileTyping(patient[HOME_NUMBER])}
                                searchInput={searchText}
                                maxLength={15}
                                textStyle={textOptionsListStyle}
                                id="header_search_patient_home_number"
                              />
                            )}
                          </Col>
                          <Col span={5}>
                            <ShorterTextWithTooltip
                              text={assigneeNames}
                              searchInput={searchText}
                              maxLength={12}
                              textStyle={textOptionsListStyle}
                              id="header_search_patient_assignee"
                            />
                          </Col>
                        </Row>
                      ),
                      patient_id: patient.patientId,
                      pharmacy_patient: patient.patientId,
                      journey_id: journeyId
                    };
                  })
                }
              ]
        }
      >
        <StyledInputMask
          mask={!enablePhoneMasking && searchTypes[searchBy]?.maskInput}
          maskPlaceholder=""
          placeholder={t(`header_searchbar.placeholder.${searchBy}`)}
          alwaysShowMask={false}
        />
      </AutoComplete>
    </StyledInputGroup>
  );
};

const mapStateToProps = createStructuredSelector({
  isSearchFetching: selectIsFetching([ApiNames.PhiSearchFreeText, ApiNames.SearchFreeText]),
  isRecentPatientsFetching: selectIsFetching([ApiNames.getRecentPatients]),
  searchedPatients: selectSearchedPatients,
  firstRecentPatients: selectFirstRecentPatients
});

const mapDispatchToProps = (dispatch) => ({
  selectSearchedPatient: (pharmacyPatient, patientId) => dispatch(ACTIONS.setPatientAction(pharmacyPatient, patientId)),
  searchPatientsAct: (page, text, searchBy) => dispatch(ACTIONS.searchPatientsAction(page, text, searchBy)),
  recentPatientsAct: (page) => dispatch(ACTIONS.getRecentPatientsAction(page, true)),
  restSearchResult: () => dispatch(ACTIONS.clearSearchedPatients()),
  setSearchResultPageAct: (wasSearchResultPage, searchBy, searchText) =>
    dispatch(ACTIONS.setSearchResultPage(wasSearchResultPage, searchBy, searchText)),
  setRecentPatientPageAct: (wasRecentPatientPage) => dispatch(ACTIONS.setRecentPatientPage(wasRecentPatientPage))
});

const HeaderSearchBar = compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(HeaderSearchbarComponent);

export { HeaderSearchBar };
